import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, LogOut, RefreshCw, Building2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Pending() {
  const { user, loading, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [choice, setChoice] = useState("");
  const [company, setCompany] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
    if (!loading && user?.approved) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);
  useEffect(() => { api.get("/orgs/public").then((r) => setOrgs(r.data)).catch(() => {}); }, []);

  const requested = orgs.find((o) => o.id === user?.requested_org_id);
  const submit = async () => {
    if (!choice) return toast.error("Pick the workspace you're requesting access to");
    if (!company.trim()) return toast.error("Please enter what company you are with");
    setSaving(true);
    try { await api.post("/auth/request-org", { org_id: choice, company: company.trim(), note: note.trim() }); toast.success("Request sent to that company's admin"); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-8" data-testid="pending-page">
      <div className="max-w-md w-full rounded-xl border border-slate-800 bg-slate-900/70 p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-amber-500/15 flex items-center justify-center"><ShieldAlert className="h-6 w-6 text-amber-400" /></div>
        <h1 className="mt-4 text-2xl font-display font-bold">Awaiting approval</h1>
        <p className="mt-2 text-sm text-slate-400">
          Thanks for signing in{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Simply Served is restricted to approved process servers and staff.
          Your request is recorded for <span className="text-slate-200" data-testid="pending-email">{user?.email}</span>.
        </p>
        {requested ? (
          <p className="mt-4 text-sm text-emerald-300" data-testid="pending-requested-org"><Building2 className="inline h-4 w-4 mr-1" />Waiting for an admin at <strong>{requested.name}</strong> to approve you.</p>
        ) : (
          <div className="mt-5 text-left space-y-3">
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-slate-400">Which workspace are you requesting access to?</label>
              <select value={choice} onChange={(e) => setChoice(e.target.value)} className="w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100" data-testid="pending-org-select">
                <option value="">Select…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-slate-400">What company are you with? <span className="text-rose-400">*</span></label>
              <input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={200} placeholder="e.g. Smith & Jones Process Serving" className="w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500" data-testid="pending-company-input" />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-slate-400">Why do you need access? <span className="normal-case text-slate-500">(optional)</span></label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={2} placeholder="Your role, who referred you, etc." className="w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 resize-none" data-testid="pending-note-input" />
            </div>
            <Button className="w-full sm-green" onClick={submit} disabled={saving || !choice || !company.trim()} data-testid="pending-org-submit">{saving ? "Sending…" : "Request access"}</Button>
          </div>
        )}
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="outline" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={refresh} data-testid="pending-refresh"><RefreshCw className="h-4 w-4 mr-2" />Check again</Button>
          <Button variant="ghost" className="text-slate-400 hover:text-white" onClick={async () => { await logout(); navigate("/login", { replace: true }); }} data-testid="pending-logout"><LogOut className="h-4 w-4 mr-2" />Sign out</Button>
        </div>
      </div>
    </div>
  );
}
